import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useAppStore } from "@/store/app-store";
import type { components } from "@/api/types";
import type {
  JobRef,
  UserDetail,
  UserExposureHistory,
  UserInvite,
  UserSummary,
} from "@/api/models";
import { toUserExposureHistory, toUserSummary } from "@/api/models";
import { trackEvent, AnalyticsEvent } from "@/analytics";

export function useUsers(filters: { role?: string; q?: string } = {}) {
  // Scope the cache by the active department so a respai_admin switching
  // departments never sees the previous tenant's roster from cache (the request
  // is dept-scoped via the X-RespAi-Department-ID header).
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useQuery({
    queryKey: ["users", deptId, filters],
    queryFn: async (): Promise<UserSummary[]> => {
      // UserRead carries no sensor time / last-incident, so derive both from the
      // department's exposure records (same client-side aggregation the overview
      // KPIs use). Exposure fetch is best-effort: a failure must not blank the
      // whole roster, so fall back to an empty list (→ 0 min / "—").
      const [rawUsers, exposures] = await Promise.all([
        api.get<components["schemas"]["UserRead"][]>("/users", {
          query: filters,
        }),
        api
          .get<components["schemas"]["ExposureRecordRead"][]>("/exposure-records")
          .catch(() => [] as components["schemas"]["ExposureRecordRead"][]),
      ]);

      const byUser = new Map<string, { seconds: number; last: string | null }>();
      for (const e of exposures) {
        const prev = byUser.get(e.user_id) ?? { seconds: 0, last: null };
        // interior_seconds is the only populated coverage field (overhaul/exterior
        // are structurally 0 — no zone telemetry on the pilot path).
        const seconds = prev.seconds + e.interior_seconds;
        // ISO-8601 UTC strings sort lexicographically, so keep the latest.
        const when = e.finalized_at ?? e.created_at;
        const last = !prev.last || when > prev.last ? when : prev.last;
        byUser.set(e.user_id, { seconds, last });
      }

      const rows = rawUsers.map((u) => {
        const summary = toUserSummary(u);
        const agg = byUser.get(u.id);
        if (agg) {
          summary.sensor_minutes_recorded = Math.round(agg.seconds / 60);
          summary.last_incident_at = agg.last;
        }
        return summary;
      });

      const q = filters.q?.toLowerCase();
      return q
        ? rows.filter((u) =>
            [u.display_name, u.email, u.badge_number ?? ""].some((v) =>
              v.toLowerCase().includes(q),
            ),
          )
        : rows;
    },
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: ["user", id],
    queryFn: async (): Promise<UserDetail> =>
      toUserSummary(
        await api.get<components["schemas"]["UserRead"]>(`/users/${id}`),
      ),
    enabled: !!id,
  });
}

export function useUserExposureHistory(id: string | undefined) {
  return useQuery({
    queryKey: ["user-exposure", id],
    // The dedicated endpoint returns the firefighter's real cumulative dose per
    // gas (Σ ppm·min) and an incident timeline with each incident's additive
    // dose — replacing the old client-side aggregation that fabricated ppm·min as
    // 0 (no /exposure-records fan-out, no hardcoded zeros).
    queryFn: async (): Promise<UserExposureHistory> =>
      toUserExposureHistory(
        await api.get<components["schemas"]["UserExposureHistory"]>(
          `/users/${id}/exposure-history`,
        ),
      ),
    enabled: !!id,
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  // A respai_admin carries no implicit department, so the invite must name the
  // target. Send the active (switcher-pinned) department explicitly — the same
  // id already travels in the X-RespAi-Department-ID header, but putting it in
  // the body makes the request self-describing and works for a tenant chief too
  // (the backend ignores it and uses the chief's own department).
  const deptId = useAppStore((s) => s.selectedDepartmentId);
  return useMutation({
    mutationFn: (input: UserInvite) =>
      api
        .post<components["schemas"]["UserRead"]>("/users/invite", {
          email: input.email,
          given_name: input.given_name,
          family_name: input.family_name,
          roles: input.roles,
          department_id: deptId ?? undefined,
        })
        .then(toUserSummary),
    onSuccess: (_user, variables) => {
      qc.invalidateQueries({ queryKey: ["users"] });
      trackEvent(AnalyticsEvent.MemberInvited, {
        role_count: variables.roles.length,
        is_multi_role: variables.roles.length > 1,
        roles: variables.roles,
      });
    },
  });
}

export function useDeleteUser() {
  // Soft-delete a user (respai_admin only, enforced server-side). They drop out of
  // the roster and can no longer authenticate; historical exposure/incident records
  // are retained. Never a hard delete.
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/users/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      trackEvent(AnalyticsEvent.MemberDeleted);
    },
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api
        .post<components["schemas"]["UserRead"]>(
          `/users/${userId}/resend-invite`,
          {},
        )
        .then(toUserSummary),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      trackEvent(AnalyticsEvent.InviteResent);
    },
  });
}

export function useGenerateUserExposureReport(userId: string) {
  return useMutation({
    mutationFn: async (range: {
      from: string;
      to: string;
    }): Promise<JobRef> => ({
      job_id: `exposure-report-${userId}-${range.from}-${range.to}`,
    }),
  });
}
